<template>
  <div
    class="w-full rounded-[16px] border-[1px] border-[#D5CFC8] bg-[#FFFFFF] p-[20px] flex flex-col gap-[16px]"
    data-test="verify-personal-id"
  >
    <div class="flex flex-col gap-[8px]">
      <h2 class="text-[1.25rem] leading-[1.75rem] font-inter font-black text-[#21201E]">
        Verify your personal ID
      </h2>
      <p class="text-[1rem] leading-[1.5rem] font-inter font-medium text-[#373531]">
        To see your giving on this account, confirm your date of birth and the last 4 characters of
        your NRIC/FIN/Passport no. You only need to do this once. You can still give in the
        meantime.
      </p>
    </div>

    <div class="w-full">
      <label :class="labelClass" for="verifyDateOfBirth">Date of Birth</label>
      <DsDatePicker
        id="verifyDateOfBirth"
        name="verifyDateOfBirth"
        v-model="dateOfBirth"
        display-format="DD-MM-YYYY"
        placeholder="Enter your Date of Birth"
        :max="today"
        :disabled="isBlocked || submitting"
        :error="touched ? dateOfBirthError : ''"
      />
    </div>
    <div class="w-full">
      <label :class="labelClass" for="verifyPartialNric">
        Last 4 chars of NRIC/FIN/Passport no.
      </label>
      <DsInput
        id="verifyPartialNric"
        name="verifyPartialNric"
        v-model="partialNric"
        placeholder="Eg: 123A"
        autocomplete="off"
        :max-length="4"
        :disabled="isBlocked || submitting"
        :error="touched ? partialNricError : ''"
      />
    </div>

    <DsAlert v-if="error" variant="error" data-test="verify-personal-id-error">
      {{ error.message }}
    </DsAlert>

    <DsButton
      variant="secondary"
      shape="pill"
      size="56"
      class="w-full"
      data-test="verify-personal-id-submit"
      :disabled="isBlocked || submitting"
      :loading="submitting"
      @click="submit"
    >
      {{ submitting ? 'Verifying...' : 'Verify' }}
    </DsButton>

    <DsLink
      v-if="error && error.kind === VERIFY_ERROR_KINDS.NO_PERSONAL_ID"
      size="sm"
      underline="always"
      class="self-center"
      data-test="verify-personal-id-profile"
      @click="goToProfile"
    >
      Go to my profile
    </DsLink>
    <ContactSupportButton v-if="error && error.offerSupport" />
  </div>
</template>

<script>
import { useStore } from 'vuex';
import {
  Alert as DsAlert,
  Button as DsButton,
  DatePicker as DsDatePicker,
  Input as DsInput,
  Link as DsLink,
} from '@team-hogc/hogc-one-design-system';
import ContactSupportButton from '@/components/common/ContactSupportButton.vue';
import { useVerifyPersonalIdForm } from '@/composables/useVerifyPersonalIdForm';

/**
 * The one-time personal-id check for a login of a member with several (giving per
 * login, @/utils/giving-login). Same fields and rules as the profile's date of birth
 * and partial NRIC (useVerifyPersonalIdForm). Asks the PIN API, through
 * myGiving/verifyPersonalId. Emits `verified` once the server accepts them.
 */
export default {
  name: 'VerifyPersonalIdCard',
  components: { ContactSupportButton, DsAlert, DsButton, DsDatePicker, DsInput, DsLink },
  emits: ['verified'],
  setup(_props, { emit }) {
    const store = useStore();
    return useVerifyPersonalIdForm({
      verify: fields => store.dispatch('myGiving/verifyPersonalId', fields),
      onVerified: result => emit('verified', result),
    });
  },
  data() {
    return {
      labelClass: 'block text-base text-primary font-inter font-medium mb-1 leading-5',
    };
  },
  methods: {
    goToProfile() {
      this.$router.push('/profile');
    },
  },
};
</script>
